import { useTranslation } from 'react-i18next';
import { Paragraph, Tag } from '@digdir/designsystemet-react';
import { agreementTones } from '../../../components/AnalysisLayout/analysis.utils';
import { DateUtils } from '../../../utils/DateUtils';
import type { SurveyListItem } from '../../../domain/surveys/survey-list';
import { ModuleIcon } from './ModuleIcon';
import { SurveyRowActions, type SurveyRowActionHandlers } from './SurveyRowActions';
import classes from '../SurveysPage.module.css';

const scoreFormat = new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (date: Date) => DateUtils.getShortDateFromDate(date, true);

type SurveyRowProps = SurveyRowActionHandlers & { item: SurveyListItem };

export const SurveyRow = ({ item, ...handlers }: SurveyRowProps) => {
  const { t } = useTranslation();
  const { count, expected, rate } = item.responses;
  const period = item.deadline
    ? t(item.isOngoing ? 'surveys.page.ongoingPeriod' : 'surveys.page.period', {
        start: formatDate(item.startDate),
        end: formatDate(item.deadline),
      })
    : formatDate(item.startDate);
  const tag = item.agreement
    ? { label: t(`teamResult.agreement.${item.agreement}`), tone: agreementTones[item.agreement] }
    : item.status;

  return (
    <li className={classes.row}>
      <div className={classes.surveyCell}>
        <ModuleIcon module={item.module} />
        <div className={classes.titleBlock}>
          <span className={classes.moduleName}>{t(`surveys.page.modules.${item.module}`)}</span>
          <span className={classes.title}>{item.title}</span>
          <span className={classes.meta}>{period}</span>
        </div>
      </div>

      <div className={classes.responsesCell}>
        {count !== null && expected ? (
          <>
            <Paragraph data-size="sm">{t('surveys.page.responses', { count, expected })}</Paragraph>
            <span aria-hidden="true" className={classes.progress}>
              <span style={{ width: `${Math.min(100, (count / expected) * 100)}%` }} />
            </span>
          </>
        ) : (
          <Paragraph data-size="sm">{t('surveys.page.responseRate', { rate: Math.round(rate) })}</Paragraph>
        )}
      </div>

      <div className={classes.resultCell}>
        <div className={classes.result}>
          {item.score && (
            <span className={classes.score} data-tone={item.score.tone}>
              <span className={classes.scoreValue}>{scoreFormat.format(item.score.value)}</span>
              <span className={classes.meta}>{t('surveys.page.scoreOf')}</span>
            </span>
          )}
          <Tag data-size="sm" data-color={tag.tone}>
            {tag.label}
          </Tag>
        </div>
        {item.note && <span className={classes.meta}>{item.note}</span>}
      </div>

      <SurveyRowActions item={item} {...handlers} />
    </li>
  );
};
